/* Copyright (c) 2026 Centrica Soluciones Innovadoras. */
import { Injectable, inject } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { DialogService } from 'nebula-ui-kit';
import { TREASURY, TranslationService } from '@nebula-shared/data-access';
import { BankAccountService } from '../../data-access/bank-account-modal/bank-account.service';
import {
  BankAccountSelectorOptions,
  CuentaBancoColumna,
  CuentaBancoPago,
  SelectDialogLoadParams,
} from '../../domain/bank-account-modal.model';
import { SelectDialogComponent } from '../../../select-dialog/select-dialog.component';

/**
 * Modal de selección de cuenta bancaria de origen (INT-ALC1-02).
 * Reutiliza SelectDialogComponent; consume GET /banco-pagos/cuentas-banco.
 *
 * <p>Es el ÚNICO selector de cuenta bancaria de la aplicación: un proceso con
 * otra presentación o con otras reglas de filtrado —los traslados bancarios
 * (HU-TES-005) muestran banco y número de cuenta, y admiten cuentas sin saldo—
 * lo ajusta por {@link BankAccountSelectorOptions}, no con un modal propio.</p>
 *
 * SAR ASR-PF-ARQ-01 (exclusión intencional): helper de UI del proceso pagar,
 * no servicio CRUD; no extiende BaseCrudService.
 */
@Injectable({ providedIn: 'root' })
export class BankAccountModalService {
  private readonly dialogService = inject(DialogService);
  private readonly translation = inject(TranslationService);
  private readonly bankPaymentService = inject(BankAccountService);

  /** Traduce una clave i18n con el idioma activo. */
  private t(key: string): string {
    return this.translation.translate(key);
  }

  /** Columnas del proceso pagar: código, nombre y saldo de la cuenta. */
  private columnasPorDefecto(): CuentaBancoColumna[] {
    const i18n = TREASURY.BANK_PAYMENTS.MODAL;
    return [
      { key: 'codCuenta', label: this.t(i18n.COL_CODE), sortable: true, width: '22%' },
      { key: 'nombreCuenta', label: this.t(i18n.COL_NAME), sortable: true, width: '48%' },
      {
        key: 'saldoDisponible',
        label: this.t(i18n.COL_BALANCE),
        sortable: true,
        pipe: 'currency',
        width: '30%',
      },
    ];
  }

  /**
   * Abre el selector de cuenta bancaria.
   *
   * @param soloChequera si es true, filtra las cuentas que operan chequera.
   *        Lo ignora quien mande `options.filtros`.
   * @param currentCodigo código de la cuenta ya seleccionada, para marcarla.
   * @param options presentación y filtros propios del proceso que lo abre.
   */
  openSelector(
    soloChequera = false,
    currentCodigo?: string,
    options: BankAccountSelectorOptions = {},
  ): Observable<CuentaBancoPago | null> {
    const i18n = TREASURY.BANK_PAYMENTS.MODAL;
    const columns = options.columns ?? this.columnasPorDefecto();
    const filtros = options.filtros ?? { soloChequera };

    /**
     * Pide al servidor la página que el diálogo esté mostrando.
     *
     * <p>Quien pagina es `SelectDialogComponent`: él lleva la página vigente y
     * el tamaño elegido y los manda en cada carga. Aquí solo se traduce la
     * página de base 1 —la del diálogo— a la base 0 que espera el backend.</p>
     */
    const loadDataFn = async (
      params: SelectDialogLoadParams,
    ): Promise<{ data: CuentaBancoPago[]; total: number }> => {
      try {
        const response = await lastValueFrom(
          this.bankPaymentService.getCuentasBanco({
            ...filtros,
            page: (params.page ?? 1) - 1,
            size: params.limit,
            searchTerm: params.search || '',
            sortColumn: params.sortColumn ?? undefined,
            sortDirection: params.sortDirection ?? undefined,
          }),
        );

        return {
          data: response.content,
          total: response.totalElements,
        };
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return { data: [], total: 0 };
      }
    };

    const dialogRef = this.dialogService.open(SelectDialogComponent, {
      data: {
        title: options.title ?? this.t(i18n.SELECT_ACCOUNT),
        columns,
        multiSelect: false,
        searchPlaceholder: options.searchPlaceholder ?? this.t(i18n.SEARCH_ACCOUNT),
        loadDataFn,
        itemIdKey: 'cuentaBancariaId',
        displayKey: 'nombre',
        selectedItems: currentCodigo ? [{ codigo: currentCodigo, nombre: currentCodigo }] : [],
      },
      size: 'lg',
      width: '760px',
    });

    return dialogRef
      .afterClosed()
      .pipe(
        map((selected: CuentaBancoPago[] | null) =>
          selected && selected.length > 0 ? selected[0] : null,
        ),
      );
  }
}
